import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { invalidateFinancials } from '../../lib/invalidate';
import { signedKr } from '../../lib/money';
import { type RouterOutputs, trpc } from '../../lib/trpc';
import { Panel } from '../common/Panel';
import { Skeleton } from '../ui/skeleton';

type Pending = RouterOutputs['enableBanking']['pendingTransactions'][number];
type Booked = RouterOutputs['transaction']['search']['items'][number];
type Selected = { kind: 'pending'; row: Pending } | { kind: 'booked'; row: Booked };

const dayFormat = new Intl.DateTimeFormat('sv-SE', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const formatDay = (date: string) =>
  dayFormat.format(new Date(`${date}T00:00:00Z`)).replace('.', '');

const pendingRowKey = (p: Pending) => `${p.accountId}|${p.date}|${p.amount}|${p.description}`;
const bookedLabel = (t: Booked) =>
  t.billName ?? t.loanName ?? t.incomeName ?? t.categoryName ?? 'Osorterad';

const PENDING_NOTE =
  'Banken har reserverat köpet men inte bokfört det än. Det är redan draget från Kvar att spendera, och det räknas med även om du döljer det. När köpet bokförs döljs det också.';
const BOOKED_NOTE =
  'Ett dolt köp syns inte i listorna men räknas ändå med i budgeten och i Kvar att spendera. Under Köp hittar du det med filtret Dolda.';

/**
 * Latest purchases in the period, with card purchases the bank hasn't booked
 * yet on top. They're off "Kvar att spendera" already, but count as purchases
 * only once booked. Tap any of them to hide it from lists; hidden ones still
 * count in every number, and a link brings them back here.
 */
export function RecentCard({
  startDate,
  lastDate,
  showPending,
}: {
  startDate: string;
  lastDate: string;
  /** Pending card purchases are "now"; only show them in the current period */
  showPending: boolean;
}) {
  const utils = trpc.useUtils();
  const [selected, setSelected] = useState<Selected | null>(null);
  const [selectedOpen, setSelectedOpen] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const listFilter = {
    dateFrom: startDate,
    dateTo: lastDate,
    isTransfer: false,
    isExcluded: false,
  };
  const recentQuery = trpc.transaction.search.useQuery({
    ...listFilter,
    isHidden: showHidden ? undefined : false,
    limit: 8,
  });
  // Only the total is used: how many purchases in the period are hidden
  const hiddenBookedQuery = trpc.transaction.search.useQuery({
    ...listFilter,
    isHidden: true,
    limit: 1,
  });
  const pendingQuery = trpc.enableBanking.pendingTransactions.useQuery(undefined, {
    staleTime: 60 * 60 * 1000, // match server cache: 1 hour
    retry: false,
    enabled: showPending,
  });

  const undoToast = (isHidden: boolean, undo: () => void) =>
    toast.success(isHidden ? 'Dold i listor, räknas ändå med' : 'Visas i listor igen', {
      action: { label: 'Ångra', onClick: undo },
    });

  const setPendingHidden = trpc.transaction.setPendingHidden.useMutation({
    onSuccess: async (_data, input) => {
      setSelectedOpen(false);
      // Served from the server's cache: no new bank request
      await utils.enableBanking.pendingTransactions.invalidate();
      undoToast(input.isHidden, () =>
        setPendingHidden.mutate({ ...input, isHidden: !input.isHidden }),
      );
    },
    onError: (error) => toast.error(error.message),
  });

  const setHidden = trpc.transaction.setHidden.useMutation({
    onSuccess: async (_data, input) => {
      setSelectedOpen(false);
      await invalidateFinancials(utils);
      undoToast(input.isHidden, () => setHidden.mutate({ ...input, isHidden: !input.isHidden }));
    },
    onError: (error) => toast.error(error.message),
  });

  const allPending = showPending ? (pendingQuery.data ?? []).filter((p) => p.amount < 0) : [];
  const hiddenCount =
    allPending.filter((p) => p.isHidden).length + (hiddenBookedQuery.data?.total ?? 0);
  const pending = showHidden ? allPending : allPending.filter((p) => !p.isHidden);
  const items = recentQuery.data?.items ?? [];

  const open = (next: Selected) => {
    setSelected(next);
    setSelectedOpen(true);
  };

  const toggleSelected = () => {
    if (!selected) return;
    if (selected.kind === 'pending') {
      const p = selected.row;
      setPendingHidden.mutate({
        accountId: p.accountId,
        date: p.date,
        amount: p.amount,
        description: p.description,
        isHidden: !p.isHidden,
      });
    } else {
      setHidden.mutate({ transactionId: selected.row.id, isHidden: !selected.row.isHidden });
    }
  };

  const describe = (s: Selected) => {
    if (s.kind === 'pending') {
      return `Väntar · ${formatDay(s.row.date)} · ${s.row.accountName}`;
    }
    const account = s.row.accountName ? ` · ${s.row.accountName}` : '';
    return `${bookedLabel(s.row)} · ${formatDay(s.row.date)}${account}`;
  };

  return (
    <Panel
      title="Senaste köpen"
      action={
        <Link to="/transactions" className="font-medium text-primary hover:underline">
          Alla köp
        </Link>
      }
    >
      {recentQuery.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : items.length === 0 && pending.length === 0 ? (
        <p className="text-sm text-muted-foreground">Inga köp i perioden än.</p>
      ) : (
        <ul className="-my-1">
          {pending.map((p) => (
            <Row
              key={`pending-${pendingRowKey(p)}`}
              description={p.description || 'Kortköp'}
              detail={`${p.isHidden ? 'Dold · ' : ''}Väntar · ${formatDay(p.date)}`}
              amount={p.amount}
              muted
              onClick={() => open({ kind: 'pending', row: p })}
            />
          ))}
          {items.map((t) => (
            <Row
              key={t.id}
              description={t.description}
              detail={`${t.isHidden ? 'Dold · ' : ''}${bookedLabel(t)} · ${formatDay(t.date)}`}
              amount={t.amount}
              muted={t.isHidden}
              onClick={() => open({ kind: 'booked', row: t })}
            />
          ))}
        </ul>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowHidden((v) => !v)}
          className="mt-2 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          {showHidden ? 'Göm dolda' : `Visa dolda (${hiddenCount})`}
        </button>
      )}

      <Dialog open={selectedOpen} onOpenChange={setSelectedOpen}>
        <DialogContent className="gap-5 sm:max-w-md">
          {selected && (
            <>
              <DialogHeader className="pr-10">
                <DialogTitle className="line-clamp-3 text-[15px] leading-snug font-semibold">
                  {selected.row.description || 'Kortköp'}
                </DialogTitle>
                <DialogDescription>{describe(selected)}</DialogDescription>
              </DialogHeader>
              <p className="text-3xl leading-none font-semibold tracking-tight tabular-nums">
                {signedKr(selected.row.amount)}
              </p>
              <p className="text-sm text-muted-foreground">
                {selected.kind === 'pending' ? PENDING_NOTE : BOOKED_NOTE}
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant={selected.row.isHidden ? 'default' : 'outline'}
                  disabled={setPendingHidden.isPending || setHidden.isPending}
                  onClick={toggleSelected}
                >
                  {selected.row.isHidden ? 'Visa i listor igen' : 'Dölj i listor'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function Row({
  description,
  detail,
  amount,
  muted = false,
  onClick,
}: {
  description: string;
  detail: string;
  amount: number;
  muted?: boolean;
  onClick: () => void;
}) {
  return (
    <li className={cn('border-b border-border/60 last:border-0', muted && 'text-muted-foreground')}>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-md py-2.5 text-left outline-none hover:bg-muted/40 focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <div className="min-w-0 flex-1">
          <p className={cn('truncate text-sm font-medium', muted && 'italic')}>{description}</p>
          <p className="truncate text-xs text-muted-foreground">{detail}</p>
        </div>
        <span
          className={cn('shrink-0 text-sm tabular-nums', amount > 0 && !muted && 'text-positive')}
        >
          {signedKr(amount)}
        </span>
      </button>
    </li>
  );
}
